import { Injectable } from "@angular/core";
import { IApiUser, IApiUserSession, UserRole } from "../interfaces/user.api.interface";
import { Store } from "@ngrx/store";
import * as SessionActions from './session.actions';
import * as SessionSelectors from './session.selectors';
import { IApiLoginCredentials, IApiSession, ILoginError } from "../interfaces/session.api.interface";
import { Observable, Subject, map, shareReplay } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { SessionApiService } from "../services/session.api.service";
import { CookieService } from "../services/cookie.api.service";
import * as CryptoJS from 'crypto-js';

@Injectable({ providedIn: 'root' })
export class SessionService {

    //Usuario de sessión
    private currentUser : IApiUserSession;

    private logoutSource = new Subject<void>();
    
    // Subject para notificar cambios en permisos
    private permissionsUpdatedSource = new Subject<{ module: number; permissions: number[], idPermission: number[] }[]>();
    public permissionsUpdated$ = this.permissionsUpdatedSource.asObservable();

    constructor(private store: Store<IApiUser>, private dialogRef: MatDialog, private sessionApiService: SessionApiService, private cookieService: CookieService) {
      this.loadSession();

      this.sessionUser$().subscribe(user => {
          this.currentUser = user;
        });
    }

    private loadSession(): void {
        try {
          if (typeof localStorage !== 'undefined' && localStorage != null && localStorage.getItem('session') != null){
            const session = JSON.parse(atob(localStorage.getItem('session'))) as IApiSession;
            this.store.dispatch(SessionActions.loginSuccess({ session }));
          }
        } catch (err) {
          console.error(err);
          var error = {error : true, errorMessage : null} as ILoginError;
          this.store.dispatch(SessionActions.loginFailure({error}));
        }
      }

    logout(): void {
      this.dialogRef.closeAll();
      console.log("Logout");
      // Limpiar el usuario actual inmediatamente
      this.currentUser = null;
      this.cookieService.removeCookie('session');
      this.store.dispatch(SessionActions.logout());
      localStorage.removeItem('googleLoginPending');
      localStorage.clear();
      sessionStorage.clear();
      this.logoutSource.next();
    }

    private sessionUser$(): Observable<IApiUserSession> {
        return this.store.select(SessionSelectors.getSessionUser).pipe(shareReplay());
    }

    sessionUserChanges$(): Observable<IApiUserSession> {
      return this.store.select(SessionSelectors.getSessionUser);
    }

    sessionLoginErrorChanges$(): Observable<ILoginError> {
      return this.store.select(SessionSelectors.getSessionError);
    }

    async googleSignIn(session : IApiUserSession){
      if (session != null){
        this.store.dispatch(SessionActions.loginSuccess({ session }))
      }
    }

    /*
    *    Getter User
    */
    get user(): IApiUserSession {
        return this.currentUser;
    }

    /**
     * Comprueba si el usuario está conectado
     * @returns true if logged
     */
    isLoggedIn(): boolean {
        return !!this.currentUser && !this.IsSessionExpired();
    }

    /**
     * Comprueba si es administrador
     */
    isAdmin(): boolean {
      return this.currentUser?.IsAdmin;
    }

    /**
    * Comprueba si el usuario que ha iniciado sesión ha especificado el turno
    *
    */
    hasSetShift(): boolean {
      return this.currentUser?.shift == null || this.currentUser?.shift == undefined;
    }

    /**
    * Comprueba si el turno en la sesión ha expirado
    */
    shiftHasExpired(): boolean {
      return this.hasSetShift() ? this.currentUser.shiftExpiration + (8 * 60 * 60 * 1000) > Math.floor(Date.now() / 1000) + (8 * 60 * 60 * 1000): false;
    }

    /**
     * Disparador de la accion Login
     * @param credentials Datos de las credenciales
     */
    login(credentials: IApiLoginCredentials): void {
      this.store.dispatch(SessionActions.login({ credentials }));
      localStorage.clear()
    }

    /**
     * Disparador de la accion loginFailure
     * @param error El error
     */
    triggerFailure(error: any): void {
      this.store.dispatch(SessionActions.loginFailure({ error }));
      localStorage.clear()
    }

    /**
     * Disparador de la accion loginSuccess
     * @param session El usuario en sesion
     */
    triggerLogInSuccess(session: IApiUserSession): void {
      this.store.dispatch(SessionActions.loginSuccess({ session }));
    }

    getInitials() : string {
      var ret = "";

      if (this.currentUser.name != null && this.currentUser.name.length > 0){
        ret = ret + this.currentUser.name.substring(0,1).toUpperCase();
      }

      if (this.currentUser.lastname1 != null && this.currentUser.lastname1.length > 0){
        ret = ret + this.currentUser.lastname1.substring(0,1).toUpperCase();
      }
      return ret;
    }

    /**
     * Comprueba si el token de acceso está expirado
     */
    private IsSessionExpired() : boolean {
        if (this.currentUser == null || this.currentUser.accessTokenExpiration == null) return true;
        return this.currentUser.accessTokenExpiration < Math.floor(Date.now() / 1000);
    }

    /**
     * Recarga los permisos del usuario
     */
    async LoadPermissions() {
      // Verificar que hay sesión válida antes de cargar permisos
      if (!this.currentUser || !this.currentUser.idUser || !this.isLoggedIn()) {
        console.log('No hay sesión activa válida, no se cargan permisos');
        return;
      }

      const idUser = this.currentUser.idUser;

      // Recuperamos la clave de cifrado del sessionStorage o la generamos si no existe
      let secretKey = sessionStorage.getItem('permissions_key');
      let key: CryptoKey;

      if (!secretKey) {
        // Generamos una clave aleatoria
        const keyMaterial = await window.crypto.subtle.generateKey(
          { name: 'AES-GCM', length: 256 }, // Clave AES de 256 bits
          true, // Si la clave es exportable (la necesitamos para cifrado y descifrado)
          ['encrypt', 'decrypt'] // Usos permitidos de la clave
        );

        // Convertimos la clave en un formato exportable y la guardamos en sessionStorage
        const jwk = await window.crypto.subtle.exportKey('jwk', keyMaterial);
        sessionStorage.setItem('permissions_key', JSON.stringify(jwk)); // Guardamos como string

        key = keyMaterial; // Guardamos la clave en formato CryptoKey
      } else {
        // Si ya existe una clave en sessionStorage, la cargamos y la importamos de nuevo
        const jwk = JSON.parse(secretKey); // Convertimos el string de vuelta a objeto
        key = await window.crypto.subtle.importKey(
          'jwk', jwk, { name: 'AES-GCM' }, true, ['encrypt', 'decrypt']
        );
      }

      let permissions: IApiUserSession['ListPermissions'] | null = null;

      // Intentamos leer los permisos desde localStorage
      const encrypted = localStorage.getItem('permissions');
      if (encrypted) {
        try {
          // Decodificamos la cadena Base64 a ArrayBuffer
          const encryptedArray = new Uint8Array(atob(encrypted).split('').map(char => char.charCodeAt(0)));

          // El nonce (IV) debe ser parte del mensaje cifrado, puede estar en los primeros 12 bytes
          const iv = encryptedArray.slice(0, 12);
          const encryptedData = encryptedArray.slice(12);

          // Desencriptamos los datos
          const decryptedBuffer = await window.crypto.subtle.decrypt(
            { name: 'AES-GCM', iv }, key, encryptedData
          );

          // Convertimos el buffer en JSON
          const decryptedData = new TextDecoder().decode(decryptedBuffer);
          permissions = JSON.parse(decryptedData);
        } catch (error) {
          console.error('Error al descifrar permisos:', error);
          permissions = null;
        }
      }

      // Si no hay permisos válidos, los pedimos al servidor
      if (!permissions) {
        permissions = await this.sessionApiService.RequestUserPermissions(idUser);

        // Ciframos los permisos con Web Crypto API
        const encoder = new TextEncoder();
        const data = encoder.encode(JSON.stringify(permissions));

        // Generamos un nonce aleatorio para AES-GCM
        const iv = window.crypto.getRandomValues(new Uint8Array(12));

        // Ciframos los datos
        const encryptedBuffer = await window.crypto.subtle.encrypt(
          { name: 'AES-GCM', iv }, key, data
        );

        // Convertimos el resultado en un Uint8Array
        const encryptedArray = new Uint8Array(encryptedBuffer);

        // Creamos un nuevo array que contenga el nonce seguido de los datos cifrados
        const concatenatedArray = new Uint8Array(iv.length + encryptedArray.length);
        concatenatedArray.set(iv, 0); // Ponemos el nonce al principio
        concatenatedArray.set(encryptedArray, iv.length); // Luego ponemos los datos cifrados

        // Convertimos el array a una cadena Base64 para almacenarlo
        const encryptedData = btoa(String.fromCharCode(...concatenatedArray));

        // Almacenamos el nonce + datos cifrados en localStorage
        localStorage.setItem('permissions', encryptedData);
      }

      // Guardamos permisos en store y usuario actual
      this.store.dispatch(SessionActions.storeUserPermissions({ permissions }));
      if (permissions) {
        this.currentUser = { ...this.currentUser, ListPermissions: permissions };
      }
    }

    /**
     * Actualiza los permisos del usuario y notifica a los suscriptores
     * @param newPermissions Nuevos permisos del usuario
     */
    async updatePermissions(newPermissions: { module: number; permissions: number[], idPermission: number[] }[]): Promise<void> {
      if (!newPermissions) {
        return;
      }

      // Actualizamos los permisos en el store
      this.store.dispatch(SessionActions.storeUserPermissions({ permissions: newPermissions }));
      
      // Actualizamos el usuario actual
      this.currentUser = { ...this.currentUser, ListPermissions: newPermissions };

      // Actualizamos localStorage con cifrado
      const idUser = this.currentUser.idUser;
      
      // Recuperamos la clave de cifrado
      let secretKey = sessionStorage.getItem('permissions_key');
      let key: CryptoKey;

      if (!secretKey) {
        // Generamos una clave aleatoria si no existe
        const keyMaterial = await window.crypto.subtle.generateKey(
          { name: 'AES-GCM', length: 256 },
          true,
          ['encrypt', 'decrypt']
        );

        const jwk = await window.crypto.subtle.exportKey('jwk', keyMaterial);
        sessionStorage.setItem('permissions_key', JSON.stringify(jwk));
        key = keyMaterial;
      } else {
        const jwk = JSON.parse(secretKey);
        key = await window.crypto.subtle.importKey(
          'jwk', jwk, { name: 'AES-GCM' }, true, ['encrypt', 'decrypt']
        );
      }

      // Ciframos los nuevos permisos
      const encoder = new TextEncoder();
      const data = encoder.encode(JSON.stringify(newPermissions));
      const iv = window.crypto.getRandomValues(new Uint8Array(12));

      const encryptedBuffer = await window.crypto.subtle.encrypt(
        { name: 'AES-GCM', iv }, key, data
      );

      const encryptedArray = new Uint8Array(encryptedBuffer);
      const concatenatedArray = new Uint8Array(iv.length + encryptedArray.length);
      concatenatedArray.set(iv, 0);
      concatenatedArray.set(encryptedArray, iv.length);

      const encryptedData = btoa(String.fromCharCode(...concatenatedArray));
      localStorage.setItem('permissions', encryptedData);

      // Notificamos a los suscriptores que los permisos han cambiado
      this.permissionsUpdatedSource.next(newPermissions);
      
      console.log('Permisos actualizados automáticamente');
    }
}
